import mixpanelBrowser from 'mixpanel-browser'

const ROLE_LABELS = {
    100: 'Super Admin',
    110: 'Master',
    120: 'Divisional',
    130: 'Portfolio',
    140: 'Product',
    150: 'Director',
    170: 'Report',
}

const OID_TYPE_MAP = {
    3: 'company',
    4: 'division',
    5: 'portfolio',
    6: 'product',
}

export const MIXPANEL_EVENTS = {
    // Survey lifecycle
    SURVEY_CREATED: 'Survey Created',
    SURVEY_PUBLISHED: 'Survey Published',
    SURVEY_CLOSED: 'Survey Closed',
    SURVEY_DUPLICATED: 'Survey Duplicated',
    SURVEY_DELETED: 'Survey Deleted',
    SURVEY_EDITED: 'Survey Edited',
    // Dashboard
    DASHBOARD_LOADED: 'Dashboard Loaded',
    DASHBOARD_FILTER_OPENED: 'Dashboard Filter Opened',
    DASHBOARD_FILTER_SAVED: 'Dashboard Filter Saved',
    DASHBOARD_FILTER_CLOSED: 'Dashboard Filter Closed',
    DASHBOARD_PINNED_FILTER_CHANGED: 'Dashboard Pinned Filter Changed',
    WIDGET_CREATOR_OPENED: 'Widget Creator Opened',
    WIDGET_CREATOR_SAVED: 'Widget Creator Saved',
    WIDGET_CREATOR_CLOSED: 'Widget Creator Closed',
    WIDGET_CREATOR_CHART_TYPE_SELECTED: 'Widget Creator Chart Type Selected',
    WIDGET_CREATOR_DATA_SOURCE_ADDED: 'Widget Creator Data Source Added',
    WIDGET_CREATOR_ADV_REPORTING_TOGGLED: 'Widget Creator Advanced Reporting Toggled',
    DASHBOARD_EDIT_STARTED: 'Dashboard Edit Started',
    DASHBOARD_EDIT_SAVED: 'Dashboard Edit Saved',
    DASHBOARD_EDIT_EXITED: 'Dashboard Edit Exited',
    // Distribution
    EMAIL_SENT: 'Email Sent',
    EMAIL_TEST_SENT: 'Email Test Sent',
    // Reporting
    REPORT_VIEWED: 'Report Viewed',
    REPORT_EXPORTED: 'Report Exported',
    // Navigation
    PAGE_VIEWED: 'Page Viewed',
    // Session
    USER_LOGGED_IN: 'User Logged In',
    USER_LOGGED_OUT: 'User Logged Out',
}

class MixpanelManager {
    constructor(mixpanel = mixpanelBrowser) {
        this.mixpanel = mixpanel
        this.initialized = false
    }

    init(token, config = {}) {
        try {
            this.mixpanel.init(token, config)
            this.initialized = true
        } catch (e) {
            console.error('MixpanelManager: init() failed', e)
        }
    }

    identify(userData, isExploriAdmin = false) {
        if (!this.guard('identify')) return

        try {
            this.mixpanel.identify(userData.userId)

            this.mixpanel.people.set({
                session_id: userData.sessionId,
                client_id: userData.clientId,
                user_role: userData.role,
                user_role_label: this.getRoleLabel(userData.role),
                explori_staff: isExploriAdmin,
                explori_reseller: userData.isReseller,
                locale: userData.locale,
                timezone: userData.timezone,
                dashboard_status: userData.dashboardStatus,
                ...this.flattenFlags('flag', userData.flags),
            })

            this.mixpanel.register({
                client_id: userData.clientId,
                user_role: userData.role,
                explori_staff: isExploriAdmin,
                explori_reseller: userData.isReseller,
            })
        } catch (e) {
            console.error('MixpanelManager: identify() failed', e)
        }
    }

    setClientProperties(client) {
        if (!this.guard('setClientProperties')) return

        try {
            const clientFlags = client.flags ?? {}
            const industryCodes = (client.industry ?? []).map((i) => i.code)

            this.mixpanel.register({
                client_survey_types: client.surveyTypes ?? [],
                client_industries: industryCodes,
                client_timezone: client.timezone,
                client_country: client.country,
                client_dashboard_status: client.dashboardStatus,
                ...this.flattenFlags('client_flag', clientFlags),
            })
        } catch (e) {
            console.error('MixpanelManager: setClientProperties() failed', e)
        }
    }

    trackDashboardLoaded({
        dashboardVersion, dashboardId, dashboardName, oId,
        recordId, accessContext, surveyId, hasActiveFilters, activeFilterCount,
    } = {}) {
        this.track(MIXPANEL_EVENTS.DASHBOARD_LOADED, {
            dashboard_version: dashboardVersion,
            dashboard_id: dashboardId,
            dashboard_name: dashboardName,
            dashboard_type: OID_TYPE_MAP[oId],
            record_id: recordId,
            access_context: accessContext,
            survey_id: surveyId,
            has_active_filters: hasActiveFilters,
            active_filter_count: activeFilterCount,
            referrer: document.referrer || undefined,
            url: window.location.href,
        })
    }

    track(eventName, properties = {}) {
        if (!this.guard('track')) return

        try {
            this.mixpanel.track(eventName, properties)
        } catch (e) {
            console.error(`MixpanelManager: track("${eventName}") failed`, e)
        }
    }

    trackPageView(path, properties = {}) {
        this.track(MIXPANEL_EVENTS.PAGE_VIEWED, { path, ...properties })
    }

    trackLogin() {
        this.track(MIXPANEL_EVENTS.USER_LOGGED_IN)
    }

    trackLogout() {
        this.track(MIXPANEL_EVENTS.USER_LOGGED_OUT)
        this.reset()
    }

    // Call on logout to dissociate the current user
    reset() {
        if (!this.initialized) return

        try {
            this.mixpanel.reset()
        } catch (e) {
            console.error('MixpanelManager: reset() failed', e)
        }
    }

    guard(methodName) {
        if (!this.initialized) {
            console.warn(`MixpanelManager: cannot call ${methodName}() before init()`)
            return false
        }
        return true
    }

    getRoleLabel(role) {
        return ROLE_LABELS[role] ?? 'Unknown'
    }

    flattenFlags(prefix, flags = {}) {
        return Object.entries(flags).reduce((acc, [key, value]) => {
            acc[`${prefix}_${key}`] = value
            return acc
        }, {})
    }
}

export { OID_TYPE_MAP }
export default MixpanelManager
